// /admin → Volunteer access — who may open Volunteer Tools, and which of them.
//
// A thin screen over /api/volunteers (built with Volunteer Tools; see its
// header for the ops). Granting is the operator's act and nobody else's, which
// is why this is on /admin and not on /volunteer: the endpoint is gated on
// `tokenIsAdmin`, and a volunteer who could grant volunteers would be an admin
// by another name.
//
// Three tools, handed out separately — somebody filling in menus has no
// business approving a club's event — so each person is a row of three
// checkboxes rather than one switch. An empty set is refused by the server
// ("remove the volunteer instead"), and the checkbox that would empty it is
// disabled here so the refusal is never the first thing somebody learns.
//
// Sign-in is an emailed link, like a club's: there is no password to show. A
// grant sends the link by default; "Copy link" mints one without mailing it,
// for when the email is not arriving.

import { useCallback, useEffect, useRef, useState } from 'react';

const FALLBACK_SCOPES = [
  { id: 'moderate', label: 'Content Moderator' },
  { id: 'issues', label: 'Issues & Reports Manager' },
  { id: 'content', label: 'Content Filler' },
  { id: 'rooms', label: 'Room Names' },
  { id: 'routes', label: 'Route tester' },
];

const when = (ts) => {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return ''; }
};

export default function VolunteerAccess({ apiBase, token, onUnauthorized }) {
  const [list, setList] = useState(null);
  const [scopes, setScopes] = useState(FALLBACK_SCOPES);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [pick, setPick] = useState(['issues']);
  const unauthRef = useRef(onUnauthorized);
  unauthRef.current = onUnauthorized;

  const call = useCallback(async (method, body) => {
    const r = await fetch(`${apiBase}/api/volunteers`, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const j = await r.json().catch(() => ({}));
    if (r.status === 401) {
      // Two different 401s: a dead session, and a live token that is not the
      // operator's (ADMIN_PASSWORD unset lets any token open /admin). Only the
      // first should throw the operator back to the login box.
      if (/operator/i.test(j.error || '')) throw new Error(j.error);
      unauthRef.current?.();
      throw new Error('Session expired — log in again.');
    }
    if (r.status === 404 && !j.error) throw new Error('The volunteer API (/api/volunteers) is not deployed here yet.');
    if (!r.ok || j.ok === false) throw new Error(j.error || 'That did not go through.');
    return j;
  }, [apiBase, token]);

  const load = useCallback(async () => {
    try {
      const j = await call('GET');
      setList(Array.isArray(j.volunteers) ? j.volunteers : []);
      if (Array.isArray(j.scopes) && j.scopes.length) setScopes(j.scopes);
    } catch (e) { setErr(e.message); setList([]); }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const act = async (key, body, done) => {
    setBusy(key); setErr(''); setMsg('');
    try {
      const j = await call('POST', body);
      if (done) done(j);
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(''); }
  };

  const grant = (e) => {
    e.preventDefault();
    act('grant', { op: 'grant', email: email.trim(), name: name.trim(), scopes: pick }, (j) => {
      setEmail(''); setName('');
      setMsg(j.sent
        ? `${j.created ? 'Added' : 'Updated'} ${j.volunteer?.email}. Their sign-in link is on its way.`
        : `${j.created ? 'Added' : 'Updated'} ${j.volunteer?.email}.${j.sendError ? ` The email did not send (${j.sendError}) — use Copy link.` : ''}`);
    });
  };

  const toggle = (v, id) => {
    const cur = v.scopes || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    act(`s-${v.id}`, { op: 'update', id: v.id, scopes: next });
  };

  const copyLink = (v) => act(`l-${v.id}`, { op: 'link', id: v.id }, (j) => {
    try { navigator.clipboard?.writeText(j.url); } catch { /* ignore */ }
    setMsg(`Sign-in link for ${v.email} copied. It works once, and expires.`);
  });

  const revoke = (v) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${v.email} from Volunteer Tools? It takes effect on their next click.`)) return;
    act(`r-${v.id}`, { op: 'revoke', id: v.id }, () => setMsg(`${v.email} no longer has access.`));
  };

  return (
    <div>
      <form style={S.card} onSubmit={grant}>
        <div style={S.cardTitle}>Grant access</div>
        <div style={S.hint}>
          They sign in with a link sent to this address — no password. Granting an address that already has access
          replaces which tools it opens.
        </div>
        <div style={S.formRow}>
          <input style={S.input} type="email" required placeholder="name@myumanitoba.ca"
                 value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
          <input style={S.input} placeholder="Name (optional)" value={name}
                 onChange={(e) => setName(e.target.value)} aria-label="Name" />
        </div>
        <div style={S.scopeRow}>
          {scopes.map((sc) => (
            <label key={sc.id} style={S.check}>
              <input type="checkbox" checked={pick.includes(sc.id)}
                     onChange={() => setPick((p) => (p.includes(sc.id) ? p.filter((x) => x !== sc.id) : [...p, sc.id]))} />
              {sc.label}
            </label>
          ))}
        </div>
        <button style={S.btnPrimary} type="submit" disabled={busy === 'grant' || !email.trim() || !pick.length}>
          {busy === 'grant' ? 'Granting…' : 'Grant and email the link'}
        </button>
      </form>

      {err && <div style={S.err}>{err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}

      <div style={S.sectionTitle}>Volunteers ({list ? list.length : '…'})</div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Who</th>
            {scopes.map((sc) => <th key={sc.id} style={S.th}>{sc.label}</th>)}
            <th style={S.th}>Last signed in</th>
            <th style={S.th} />
          </tr></thead>
          <tbody>
            {(list || []).map((v, i) => (
              <tr key={v.id} style={i % 2 ? S.trAlt : undefined}>
                <td style={S.td}>
                  <div style={{ fontWeight: 700 }}>{v.note || v.username}</div>
                  <div style={S.dim}>{v.email}</div>
                </td>
                {scopes.map((sc) => {
                  const on = (v.scopes || []).includes(sc.id);
                  const last = on && (v.scopes || []).length === 1;
                  return (
                    <td key={sc.id} style={S.td}>
                      <input type="checkbox" checked={on} disabled={!!busy || last}
                             title={last ? 'The last tool — remove the volunteer instead' : undefined}
                             onChange={() => toggle(v, sc.id)} aria-label={`${sc.label} for ${v.email}`} />
                    </td>
                  );
                })}
                <td style={S.td}>{v.lastSignInAt ? when(v.lastSignInAt) : <span style={S.dim}>never</span>}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  <button style={S.linkBtn} disabled={!!busy} onClick={() => copyLink(v)}>Copy link</button>
                  {' · '}
                  <button style={{ ...S.linkBtn, color: '#c5221f' }} disabled={!!busy} onClick={() => revoke(v)}>Remove</button>
                </td>
              </tr>
            ))}
            {list && list.length === 0 && (
              <tr><td style={S.td} colSpan={scopes.length + 3}>Nobody has access yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  card: { background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  cardTitle: { fontSize: 15, fontWeight: 800 },
  hint: { fontSize: 12.5, color: '#5f6368', lineHeight: 1.45 },
  formRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  input: { flex: '1 1 220px', fontSize: 14, padding: '9px 11px', border: '1.5px solid #dadce0', borderRadius: 8, outline: 'none', minWidth: 0 },
  scopeRow: { display: 'flex', gap: 14, flexWrap: 'wrap' },
  check: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5, cursor: 'pointer' },
  btnPrimary: { alignSelf: 'flex-start', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  ok: { background: '#e6f4ea', color: '#137333', borderRadius: 8, padding: '8px 11px', fontSize: 13, marginBottom: 12 },
  sectionTitle: { fontSize: 12, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 8px' },
  tableWrap: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e8eaed', color: '#5f6368', fontWeight: 700, whiteSpace: 'nowrap' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f3f4', verticalAlign: 'middle' },
  trAlt: { background: '#fafbfc' },
  dim: { color: '#80868b', fontSize: 12 },
  linkBtn: { background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#1a73e8', fontWeight: 700, cursor: 'pointer' },
};
